import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { PremiumPerformanceTrend } from '@coffeeplexity/shared';
import { LineChart } from 'react-native-chart-kit';

const { width } = Dimensions.get('window');

interface PerformanceTrendsViewProps {
  trends: PremiumPerformanceTrend[];
}

export const PerformanceTrendsView: React.FC<PerformanceTrendsViewProps> = ({
  trends,
}) => {
  const [selectedTrend, setSelectedTrend] =
    useState<PremiumPerformanceTrend | null>(
      trends && trends.length > 0 ? trends[0] : null
    );

  const getTrendColor = (direction: string) => {
    switch (direction) {
      case 'up':
        return '#10B981';
      case 'down':
        return '#EF4444';
      case 'stable':
        return '#6B7280';
      case 'volatile':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case 'up':
        return 'trending-up';
      case 'down':
        return 'trending-down';
      case 'stable':
        return 'remove';
      case 'volatile':
        return 'pulse';
      default:
        return 'remove';
    }
  };

  const formatChartData = (trend: PremiumPerformanceTrend) => {
    const labels = trend.dataPoints.map((point, index) => {
      const date = new Date(point.timestamp);
      if (trend.timeframe === 'daily') {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
      } else if (trend.timeframe === 'weekly') {
        return `W${Math.floor(index / 7) + 1}`;
      } else if (trend.timeframe === 'monthly') {
        return date.toLocaleDateString('en-US', { month: 'short' });
      }
      return `${index + 1}`;
    });

    const data = trend.dataPoints.map(point => point.value);

    return {
      labels: labels.slice(-6), // Show last 6 data points
      datasets: [
        {
          data: data.slice(-6),
          color: (_opacity = 1) => getTrendColor(trend.trendDirection),
          strokeWidth: 3,
        },
      ],
    };
  };

  const renderTrendCard = (trend: PremiumPerformanceTrend) => {
    const isSelected = selectedTrend?.id === trend.id;

    return (
      <TouchableOpacity
        key={trend.id}
        style={[styles.trendCard, isSelected && styles.selectedTrendCard]}
        onPress={() => setSelectedTrend(trend)}
      >
        <View style={styles.trendHeader}>
          <Text style={[styles.trendMetric, isSelected && styles.selectedText]}>
            {trend.metric}
          </Text>
          <View style={styles.trendIndicator}>
            <Ionicons
              name={getTrendIcon(trend.trendDirection) as any}
              size={16}
              color={getTrendColor(trend.trendDirection)}
            />
            <Text
              style={[
                styles.changeRate,
                { color: getTrendColor(trend.trendDirection) },
              ]}
            >
              {trend.changeRate > 0 ? '+' : ''}
              {trend.changeRate.toFixed(1)}%
            </Text>
          </View>
        </View>

        <Text style={styles.timeframe}>
          {trend.timeframe.charAt(0).toUpperCase() + trend.timeframe.slice(1)}{' '}
          trend
        </Text>

        <Text style={styles.significance}>
          {Math.round(trend.significance * 100)}% statistical significance
        </Text>
      </TouchableOpacity>
    );
  };

  const renderChart = () => {
    if (!selectedTrend) return null;

    const chartData = formatChartData(selectedTrend);

    return (
      <View style={styles.chartContainer}>
        <Text style={styles.chartTitle}>{selectedTrend.metric} Trend</Text>

        <LineChart
          data={chartData}
          width={width - 40}
          height={220}
          chartConfig={{
            backgroundColor: '#FFFFFF',
            backgroundGradientFrom: '#FFFFFF',
            backgroundGradientTo: '#FFFFFF',
            decimalPlaces: 1,
            color: (_opacity = 1) =>
              getTrendColor(selectedTrend.trendDirection),
            labelColor: (_opacity = 1) => '#6B7280',
            style: {
              borderRadius: 16,
            },
            propsForDots: {
              r: '4',
              strokeWidth: '2',
              stroke: getTrendColor(selectedTrend.trendDirection),
            },
            propsForBackgroundLines: {
              strokeDasharray: '',
              stroke: '#E5E7EB',
              strokeWidth: 1,
            },
          }}
          bezier
          style={styles.chart}
        />
      </View>
    );
  };

  const renderPrediction = () => {
    if (!selectedTrend) return null;

    const { prediction } = selectedTrend;

    return (
      <View style={styles.predictionContainer}>
        <Text style={styles.sectionTitle}>Prediction</Text>

        <View style={styles.predictionCard}>
          <View style={styles.predictionHeader}>
            <Ionicons name='telescope-outline' size={24} color='#6750A4' />
            <Text style={styles.predictionTitle}>Next Period Forecast</Text>
          </View>

          <View style={styles.predictionValue}>
            <Text style={styles.predictedValue}>
              {prediction.nextPeriodValue.toFixed(1)}
            </Text>
            <Text style={styles.predictionRange}>
              Range: {prediction.range[0].toFixed(1)} -{' '}
              {prediction.range[1].toFixed(1)}
            </Text>
          </View>

          <View style={styles.confidenceContainer}>
            <Text style={styles.confidenceLabel}>Confidence</Text>
            <View style={styles.confidenceBar}>
              <View
                style={[
                  styles.confidenceFill,
                  { width: `${prediction.confidence * 100}%` },
                ]}
              />
            </View>
            <Text style={styles.confidenceValue}>
              {Math.round(prediction.confidence * 100)}%
            </Text>
          </View>

          <Text style={styles.accuracyNote}>
            Historical accuracy: {Math.round(prediction.accuracy * 100)}%
          </Text>
        </View>
      </View>
    );
  };

  const renderFactors = () => {
    if (!selectedTrend) return null;

    const { prediction } = selectedTrend;

    return (
      <View style={styles.factorsContainer}>
        <Text style={styles.sectionTitle}>Influencing Factors</Text>

        {prediction.factors.map((factor, index) => (
          <View key={index} style={styles.factorCard}>
            <View style={styles.factorHeader}>
              <Text style={styles.factorName}>{factor.factor}</Text>
              <View style={styles.factorDirection}>
                <Ionicons
                  name={
                    factor.direction === 'positive'
                      ? 'arrow-up-circle'
                      : factor.direction === 'negative'
                        ? 'arrow-down-circle'
                        : 'remove-circle'
                  }
                  size={16}
                  color={
                    factor.direction === 'positive'
                      ? '#10B981'
                      : factor.direction === 'negative'
                        ? '#EF4444'
                        : '#6B7280'
                  }
                />
              </View>
            </View>

            <View style={styles.factorMetrics}>
              <View style={styles.factorMetric}>
                <Text style={styles.factorMetricLabel}>Weight</Text>
                <Text style={styles.factorMetricValue}>
                  {Math.round(factor.weight * 100)}%
                </Text>
              </View>
              <View style={styles.factorMetric}>
                <Text style={styles.factorMetricLabel}>Confidence</Text>
                <Text style={styles.factorMetricValue}>
                  {Math.round(factor.confidence * 100)}%
                </Text>
              </View>
            </View>
          </View>
        ))}
      </View>
    );
  };

  const renderInsights = () => {
    if (!selectedTrend) return null;

    return (
      <View style={styles.insightsContainer}>
        <Text style={styles.sectionTitle}>Key Insights</Text>

        {selectedTrend.insights.map((insight, index) => (
          <View key={index} style={styles.insightItem}>
            <Ionicons name='bulb-outline' size={16} color='#F59E0B' />
            <Text style={styles.insightText}>{insight}</Text>
          </View>
        ))}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance Trends</Text>
        <Text style={styles.sectionSubtitle}>
          Track your learning progress over time with predictive insights
        </Text>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.trendsScrollContainer}
        >
          {trends.map(renderTrendCard)}
        </ScrollView>
      </View>

      {selectedTrend && (
        <>
          {renderChart()}
          {renderPrediction()}
          {renderFactors()}
          {renderInsights()}
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 16,
  },
  trendsScrollContainer: {
    paddingRight: 20,
    gap: 12,
  },
  trendCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: 200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectedTrendCard: {
    borderColor: '#6750A4',
  },
  trendHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  trendMetric: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  selectedText: {
    color: '#6750A4',
  },
  trendIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  changeRate: {
    fontSize: 14,
    fontWeight: '600',
  },
  timeframe: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  significance: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  chartContainer: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    marginBottom: 24,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  chartTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 16,
    textAlign: 'center',
  },
  chart: {
    borderRadius: 16,
  },
  predictionContainer: {
    marginHorizontal: 20,
    marginBottom: 24,
  },
  predictionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  predictionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  predictionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  predictionValue: {
    alignItems: 'center',
    marginBottom: 16,
  },
  predictedValue: {
    fontSize: 32,
    fontWeight: '700',
    color: '#6750A4',
  },
  predictionRange: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 4,
  },
  confidenceContainer: {
    marginBottom: 12,
  },
  confidenceLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  confidenceBar: {
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 4,
  },
  confidenceFill: {
    height: '100%',
    backgroundColor: '#6750A4',
    borderRadius: 4,
  },
  confidenceValue: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'right',
  },
  accuracyNote: {
    fontSize: 12,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  factorsContainer: {
    marginHorizontal: 20,
    marginBottom: 24,
  },
  factorCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  factorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  factorName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1F2937',
    flex: 1,
  },
  factorDirection: {
    marginLeft: 8,
  },
  factorMetrics: {
    flexDirection: 'row',
    gap: 16,
  },
  factorMetric: {
    alignItems: 'center',
  },
  factorMetricLabel: {
    fontSize: 12,
    color: '#6B7280',
  },
  factorMetricValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  insightsContainer: {
    marginHorizontal: 20,
    marginBottom: 24,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    gap: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  insightText: {
    fontSize: 14,
    color: '#4B5563',
    flex: 1,
    lineHeight: 18,
  },
});
